/**
 * The campaign page's pure decisions (ring R4): what a failed read of the
 * campaign means for the page, and which note "fill the queue" leaves.
 *
 * Both came up after a side effect. "Call the next one now" and "fill the
 * queue" reload the campaign, and a reload that failed for a moment must not
 * swap the page for "campaign not found" while a call may be ringing. A fill
 * may add urgent defects past a full queue and file client tasks in the same
 * press, and the note has to say what actually happened.
 *
 * React-free, so it runs under plain node.
 */
import { ApiError } from "./api";
import type { EnrolOutcome } from "./aiCampaigns";

/** A failed read of one campaign: gone (404), or only not read this time (anything else). */
export function campaignLoadFailure(err: unknown): "not_found" | "failed" {
  return err instanceof ApiError && err.status === 404 ? "not_found" : "failed";
}

export interface EnrolNote {
  /** "added": how many were added; "full": the queue had no room and nothing was added. */
  kind: "added" | "full";
  tone: "ok" | "info";
  /** Client defect tasks the same press filed; 0 hides the line. */
  clientTasks: number;
}

/**
 * The fill note. Companies added are said first, even with no room left: an
 * alert campaign takes urgent defects past a full queue. "The queue is full" is
 * said only when nothing was added.
 */
export function enrolNote(outcome: Pick<EnrolOutcome, "added" | "room" | "clientTasks">): EnrolNote {
  const tasks = outcome.clientTasks;
  const clientTasks = typeof tasks === "number" && Number.isFinite(tasks) ? Math.max(0, Math.trunc(tasks)) : 0;
  if (outcome.added > 0) return { kind: "added", tone: "ok", clientTasks };
  if (outcome.room === 0) return { kind: "full", tone: "info", clientTasks };
  return { kind: "added", tone: "info", clientTasks };
}
